/**
 * Auth Fetch
 * Central fetch wrapper with automatic 401 handling and token refresh
 */ import { clearQueue, isRefreshInProgress, processQueue, queueRequest, rejectQueue, setRefreshInProgress } from './requestQueue';
import { getStoredTokenExpiry, getTimeRemaining, getTokenExpiryTime, REFRESH_BUFFER_MS, setTokenExpiry } from './tokenManager';
// Callbacks set by AuthProvider
let onRefreshSuccess = null;
let onRefreshFailure = null;
/**
 * Set callbacks for refresh events (called by AuthProvider)
 */ export function setAuthFetchCallbacks(callbacks) {
    onRefreshSuccess = callbacks.onRefreshSuccess ?? null;
    onRefreshFailure = callbacks.onRefreshFailure ?? null;
}
/**
 * Attempt to refresh the access token.
 * Returns a RefreshOutcome so callers can distinguish a dead session (401/403)
 * from a transient hiccup (5xx / network) without relying on clock heuristics.
 */ export async function refreshAccessToken() {
    try {
        const response = await fetch('/api/auth/refresh', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (!response.ok) {
            // 401/403 → the refresh cookie is invalid; session is definitively dead
            if (response.status === 401 || response.status === 403) {
                return {
                    status: 'expired'
                };
            }
            // 5xx or anything else → transient; don't evict
            return {
                status: 'transient'
            };
        }
        const data = await response.json();
        const expiresIn = data.expires_in;
        if (typeof expiresIn !== 'number') {
            return {
                status: 'transient'
            };
        }
        return {
            status: 'ok',
            expiresIn
        };
    } catch  {
        return {
            status: 'transient'
        };
    }
}
const REFRESH_LOCK_NAME = 'auth-refresh';
/**
 * Seconds left on a token another tab refreshed, or null if the shared expiry
 * has not advanced past `floorMs`.
 */ function adoptableExpiresIn(floorMs, minRemainingMs) {
    const stored = getStoredTokenExpiry();
    if (stored === null || stored <= floorMs) return null;
    const remainingMs = stored - Date.now();
    return remainingMs > minRemainingMs ? Math.floor(remainingMs / 1000) : null;
}
/**
 * One cross-tab-safe refresh attempt. Runs under the 'auth-refresh' Web Lock
 * when available, so tabs sharing one rotating cookie refresh one at a time;
 * a tab that wins the lock after another tab already refreshed adopts that
 * tab's expiry instead of calling the endpoint again. A 401/403 is likewise
 * not final if the shared expiry advanced meanwhile — another tab won the race.
 */ async function refreshAcrossTabs() {
    // Captured before waiting on the lock: only an expiry beyond this tab's own
    // counts as another tab's refresh. Our own still-fresh expiry on a rejected
    // cookie (key rotation, server-side revoke) must not suppress the refresh.
    const knownExpiry = getTokenExpiryTime() ?? 0;
    const attempt = async ()=>{
        const startedAt = Date.now();
        const fresh = adoptableExpiresIn(knownExpiry, REFRESH_BUFFER_MS);
        if (fresh !== null) return {
            status: 'ok',
            expiresIn: fresh
        };
        const outcome = await refreshAccessToken();
        if (outcome.status === 'expired') {
            const advanced = adoptableExpiresIn(Math.max(knownExpiry, startedAt), 0);
            if (advanced !== null) return {
                status: 'ok',
                expiresIn: advanced
            };
        }
        return outcome;
    };
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return navigator.locks.request(REFRESH_LOCK_NAME, attempt);
    }
    return attempt();
}
// In-flight refresh promise shared across REST and Connect callers.
// Any concurrent auth failure awaits the same single refresh rather than
// racing to POST /api/auth/refresh multiple times.
let _refreshInFlight = null;
/**
 * Deduplicated refresh: calls refreshAccessToken once per refresh cycle,
 * coalescing any concurrent callers onto the same promise.
 *
 * Callbacks fire exactly once per cycle, inside the shared .then() handler,
 * before any caller resumes — so even if REST and Connect both 401 in the
 * same cycle, onRefreshSuccess / onRefreshFailure each run once.
 *
 * Callers receive the full RefreshOutcome for local bookkeeping (re-arming
 * timers on 'transient') but must NOT call the global failure callback
 * themselves — coordinatedRefresh owns that responsibility.
 */ export async function coordinatedRefresh() {
    if (!_refreshInFlight) {
        _refreshInFlight = refreshAcrossTabs().then((outcome)=>{
            if (outcome.status === 'ok') {
                setTokenExpiry(outcome.expiresIn);
                onRefreshSuccess?.(outcome.expiresIn);
            } else if (outcome.status === 'expired') {
                // Session definitively dead — notify once, here, so concurrent REST
                // and Connect callers that both await this promise don't each fire
                // the callback independently.
                onRefreshFailure?.();
            }
            return outcome;
        }).finally(()=>{
            // Always null the in-flight promise so the next call starts a fresh
            // refresh. refreshAccessToken() never rejects (blanket catch), but
            // .finally() makes this self-healing if that ever changes.
            _refreshInFlight = null;
        });
    }
    return _refreshInFlight;
}
/**
 * Auth-aware fetch wrapper
 * - Automatically handles 401 responses by refreshing the token
 * - Queues concurrent requests during refresh to avoid multiple refresh attempts
 * - Retries the original request after successful refresh
 */ export async function authFetch(url, init) {
    // Merge credentials: 'include' into init
    const fetchInit = {
        ...init,
        credentials: 'include'
    };
    // If a refresh is already in progress, queue this request
    if (isRefreshInProgress()) {
        return new Promise((resolve, reject)=>{
            queueRequest(url, fetchInit, resolve, reject);
        });
    }
    // Make the initial request
    const response = await fetch(url, fetchInit);
    // If not a 401, return the response as-is
    if (response.status !== 401) {
        return response;
    }
    // Handle 401 — attempt token refresh, coalescing concurrent callers.
    // setRefreshInProgress gates other authFetch callers into the queue.
    // Reactive 401 paths intentionally do NOT cancel scheduled timers — the
    // token is already near-dead by the time a reactive 401 lands, so the
    // window for an expiry-timer double-fire is negligible and self-correcting
    // (onRefreshSuccess clears the expired state on the next tick).
    setRefreshInProgress(true);
    try {
        const outcome = await coordinatedRefresh();
        if (outcome.status === 'ok') {
            const retryResponse = await fetch(url, fetchInit);
            // Clear the gate before draining the queue so any authFetch call that
            // arrives during processQueue falls through rather than re-queuing onto
            // the now-empty queue and hanging forever.
            setRefreshInProgress(false);
            await processQueue(fetch);
            return retryResponse;
        }
        setRefreshInProgress(false);
        if (outcome.status === 'expired') {
            // coordinatedRefresh already fired onRefreshFailure once for all callers.
            rejectQueue(new Error('Session expired'));
        } else {
            // Transient failure: re-arm timers against remaining lifetime so the
            // user can retry without being bounced to the login page.
            const remainingMs = getTimeRemaining();
            if (remainingMs > 0) {
                setTokenExpiry(Math.ceil(remainingMs / 1000));
            }
            rejectQueue(new Error('Could not refresh session. Please try again.'));
        }
        return response;
    } catch (error) {
        setRefreshInProgress(false);
        rejectQueue(error instanceof Error ? error : new Error('Refresh failed'));
        return response;
    }
}
/**
 * Reset auth fetch state (called on logout)
 */ export function resetAuthFetch() {
    clearQueue();
}
